import React, { useState, useEffect, useCallback } from 'react'
import { apiGet, apiPost, apiDelete, getErrorMessage } from '../../utils/api'

interface Department { id: string; name: string; description?: string; member_count?: number }
interface Member { id: string; name: string; email: string; role?: string; avatar_url?: string | null }
interface AgentLite { id: string; name: string; email: string }

export const DepartmentsManager: React.FC = () => {
  const [departments, setDepartments] = useState<Department[]>([])
  const [agents, setAgents] = useState<AgentLite[]>([])
  const [members, setMembers] = useState<Record<string, Member[]>>({})
  const [expanded, setExpanded] = useState<string | null>(null)
  const [newName, setNewName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    try {
      const [d, a] = await Promise.all([
        apiGet<{ departments: Department[] }>('/departments'),
        apiGet<{ agents: AgentLite[] }>('/agents'),
      ])
      setDepartments(d.departments || [])
      setAgents(a.agents || [])
    } catch (e) {
      setError(getErrorMessage(e, 'Failed to load departments'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const loadMembers = useCallback(async (deptId: string) => {
    try {
      const r = await apiGet<{ members: Member[] }>(`/departments/${deptId}/members`)
      setMembers((prev) => ({ ...prev, [deptId]: r.members || [] }))
    } catch { /* non-critical */ }
  }, [])

  const toggle = (deptId: string) => {
    const next = expanded === deptId ? null : deptId
    setExpanded(next)
    if (next && !members[next]) loadMembers(next)
  }

  const createDept = async () => {
    if (!newName.trim()) return
    try {
      await apiPost('/departments', { name: newName.trim() })
      setNewName('')
      load()
    } catch (e) { setError(getErrorMessage(e, 'Failed to create department')) }
  }

  const addMember = async (deptId: string, agentId: string) => {
    if (!agentId) return
    try {
      await apiPost(`/departments/${deptId}/members`, { agent_id: agentId })
      await loadMembers(deptId)
      load()
    } catch (e) { setError(getErrorMessage(e, 'Failed to add member')) }
  }

  const removeMember = async (deptId: string, agentId: string) => {
    try {
      await apiDelete(`/departments/${deptId}/members/${agentId}`)
      await loadMembers(deptId)
      load()
    } catch (e) { setError(getErrorMessage(e, 'Failed to remove member')) }
  }

  const availableAgents = (deptId: string) => {
    const inDept = new Set((members[deptId] || []).map((m) => String(m.id)))
    return agents.filter((a) => !inDept.has(String(a.id)))
  }

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-base font-bold text-txt-primary">Departments</h2>
          <p className="text-xs text-txt-secondary">Group agents into departments so the widget can route chats to the right team.</p>
        </div>
      </div>

      {error && <div className="mb-3 px-3 py-2 rounded-lg bg-error-light border border-error/20 text-error text-xs">{error}</div>}

      {/* Create */}
      <div className="flex items-center gap-2 mb-4">
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && createDept()}
          placeholder="New department name (e.g. Sales)"
          className="flex-1 max-w-xs px-3 py-2 rounded-lg bg-surface border border-border text-sm text-txt-primary outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500/20"
        />
        <button onClick={createDept} className="px-3.5 py-2 rounded-lg text-sm font-medium bg-brand-600 text-white hover:bg-brand-700 transition-colors">
          Add department
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-txt-tertiary">Loading…</p>
      ) : departments.length === 0 ? (
        <p className="text-sm text-txt-tertiary">No departments yet. Create one above.</p>
      ) : (
        <div className="space-y-2">
          {departments.map((d) => (
            <div key={d.id} className="rounded-xl border border-border bg-surface">
              <button onClick={() => toggle(d.id)} className="w-full flex items-center justify-between px-4 py-3 hover:bg-surface-hover transition-colors">
                <span className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-txt-primary">{d.name}</span>
                  <span className="text-[11px] font-medium text-txt-secondary bg-surface-secondary border border-border px-1.5 py-0.5 rounded-full">
                    {d.member_count ?? 0} member{(d.member_count ?? 0) !== 1 ? 's' : ''}
                  </span>
                </span>
                <svg className={`w-4 h-4 text-txt-tertiary transition-transform ${expanded === d.id ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
              </button>
              {expanded === d.id && (
                <div className="px-4 pb-4 border-t border-border">
                  <div className="mt-3 space-y-1.5">
                    {(members[d.id] || []).length === 0 ? (
                      <p className="text-xs text-txt-tertiary">No agents in this department yet.</p>
                    ) : (
                      (members[d.id] || []).map((m) => (
                        <div key={m.id} className="flex items-center justify-between gap-2 py-1">
                          <span className="text-sm text-txt-primary truncate">{m.name} <span className="text-xs text-txt-tertiary">· {m.email}</span></span>
                          <button onClick={() => removeMember(d.id, m.id)} className="text-xs text-error hover:underline shrink-0">Remove</button>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <select
                      defaultValue=""
                      onChange={(e) => { addMember(d.id, e.target.value); e.target.value = '' }}
                      className="px-3 py-1.5 rounded-lg bg-surface border border-border text-sm text-txt-primary outline-none focus:border-brand-500"
                    >
                      <option value="" disabled>Add a team member…</option>
                      {availableAgents(d.id).map((a) => (
                        <option key={a.id} value={a.id}>{a.name} ({a.email})</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
